import { useAuthStore } from '@/stores/auth.store';

/**
 * Satisfied only by the exact permission: neither '*' nor 'zoho.*' grants them (design 10.5, P33).
 * The same set as EXPLICIT_ONLY_PERMISSIONS in apps/api/src/common/guards/roles.guard.ts — keep the
 * two identical, or the screen offers what the API refuses.
 */
export const EXPLICIT_ONLY_PERMISSIONS: ReadonlySet<string> = new Set(['zoho.owner', 'zoho.accountant']);

/**
 * Whether `granted` covers `permission`, by the API guard's rules (roles.guard.ts hasAnyPermission): the
 * exact string; then, except for an explicit-only permission, '*' (super-admin) or the module wildcard
 * ('olt.*' covers 'olt.write'). For every permission outside EXPLICIT_ONLY_PERMISSIONS the answer is the
 * one this hook always gave.
 */
export function permissionGranted(granted: readonly string[] | null | undefined, permission: string): boolean {
  if (!granted) return false;
  if (granted.includes(permission)) return true;
  if (EXPLICIT_ONLY_PERMISSIONS.has(permission)) return false;
  if (granted.includes('*')) return true;
  const module = permission.split('.')[0];
  return granted.includes(`${module}.*`);
}

export function usePermissions() {
  const user = useAuthStore((state) => state.user);

  /**
   * Check if the user has a specific permission code.
   * Permissions are stored as flat strings like 'subscribers.create', 'billing.read', etc.
   */
  function hasPermission(permission: string): boolean {
    return permissionGranted(user?.permissions, permission);
  }

  /**
   * Alias for hasPermission — check dot-notation permission string.
   * Example: canAccess('subscribers.create')
   */
  function canAccess(permission: string): boolean {
    return hasPermission(permission);
  }

  /**
   * Check if user has any of the given permissions.
   */
  function hasAnyPermission(permissions: string[]): boolean {
    return permissions.some((p) => canAccess(p));
  }

  return {
    hasPermission,
    canAccess,
    hasAnyPermission,
    roleId: user?.roleId ?? null,
    /** Zoho Books mode on/off and the owner's settings: an exact `zoho.owner` grant only. */
    canZohoOwner: hasPermission('zoho.owner'),
    /** The accountant's work (mappings, import files, cut-off register, findings): an exact grant only. */
    canZohoAccountant: hasPermission('zoho.accountant'),
    /** Work the "To invoice in Zoho" list: claim, Done manually, Adopt Zoho invoice. */
    canZohoReview: hasPermission('zoho.review'),
    /** Customer matching; whoever matched before R2 keeps it through billing.accounting (the API accepts either). */
    canZohoMatch: hasPermission('zoho.match') || hasPermission('billing.accounting'),
  };
}
